import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { api } from "~/api";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/card";
import { InputWithIcon } from "~/components/input";
import { SessionExpired } from "~/components/SessionExpired";
import {
  countSettingsOverrides,
  getSettingsOverrideFiles,
  type SettingsOverrides,
  YamlCodeBlock,
} from "~/components/settings-overrides/helpers";
import { highlightSearchText } from "~/components/settings-overrides/searchHighlight";
import { useAuth } from "~/contexts/auth";
import type { Cluster } from "~/types";

interface ClusterSettingsOverridesEntry {
  cluster: Cluster;
  settingsOverrides: SettingsOverrides;
  files: ClusterSettingsOverridesFile[];
  settingCount: number;
}

interface ClusterSettingsOverridesFile {
  fileName: string;
  searchText: string;
  settingCount: number;
}

export default function SettingsOverridesPage() {
  const [clusters, setClusters] = useState<Cluster[]>([]);
  const [query, setQuery] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { status, setIsAuthorized } = useAuth();

  useEffect(() => {
    setIsLoading(true);
    api
      .getClusters()
      .then(setClusters)
      .catch((err) => {
        if (err.status === 401) {
          setIsAuthorized(false);
        } else {
          setError(err.message);
        }
      })
      .finally(() => setIsLoading(false));
  }, [setIsAuthorized]);

  const entries = useMemo(
    () => clusters.flatMap((cluster) => buildSettingsOverridesEntry(cluster)),
    [clusters],
  );

  const searchTerms = useMemo(
    () =>
      query
        .toLowerCase()
        .split(/\s+/)
        .filter((term) => term.length > 0),
    [query],
  );

  const filteredEntries = useMemo(() => {
    if (searchTerms.length === 0) {
      return entries;
    }

    return entries.flatMap((entry): ClusterSettingsOverridesEntry[] => {
      const matchingFiles = entry.files.filter((file) =>
        searchTerms.every((term) => file.searchText.includes(term)),
      );

      if (matchingFiles.length === 0) {
        return [];
      }

      return [
        {
          ...entry,
          files: matchingFiles,
          settingCount: sumFileSettings(matchingFiles),
        },
      ];
    });
  }, [entries, searchTerms]);

  if (status !== "authorized") {
    return <SessionExpired />;
  }

  if (error) {
    return <div className="text-red-500">Error: {error}</div>;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Settings Overrides</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <InputWithIcon
              placeholder="Filter settings..."
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              iconName="Search"
              iconSize={16}
              iconClassName="text-muted-foreground"
              className="flex-1"
            />
            <div className="text-sm text-gray-500">
              {settingsOverridesResultCountLabel({
                isLoading,
                filteredCount: filteredEntries.length,
                totalCount: entries.length,
              })}
            </div>
          </div>

          <SettingsOverridesResults
            isLoading={isLoading}
            entries={entries}
            filteredEntries={filteredEntries}
            searchTerms={searchTerms}
          />
        </div>
      </CardContent>
    </Card>
  );
}

function SettingsOverridesResults({
  isLoading,
  entries,
  filteredEntries,
  searchTerms,
}: {
  isLoading: boolean;
  entries: ClusterSettingsOverridesEntry[];
  filteredEntries: ClusterSettingsOverridesEntry[];
  searchTerms: string[];
}) {
  if (isLoading) {
    return (
      <SettingsOverridesEmptyState message="Loading settings overrides..." />
    );
  }

  if (filteredEntries.length === 0) {
    let message = "No settings overrides match this search.";
    if (entries.length === 0) {
      message = "No clusters currently have settings overrides.";
    }

    return <SettingsOverridesEmptyState message={message} />;
  }

  return (
    <div className="space-y-4">
      {filteredEntries.map((entry) => (
        <ClusterSettingsOverridesCard
          key={entry.cluster.id}
          entry={entry}
          searchTerms={searchTerms}
        />
      ))}
    </div>
  );
}

function SettingsOverridesEmptyState({ message }: { message: string }) {
  return (
    <div className="rounded-md border border-dashed border-gray-300 bg-white p-6 text-center text-sm text-gray-500">
      {message}
    </div>
  );
}

function settingsOverridesResultCountLabel({
  isLoading,
  filteredCount,
  totalCount,
}: {
  isLoading: boolean;
  filteredCount: number;
  totalCount: number;
}) {
  if (isLoading) {
    return "Loading clusters...";
  }

  return `Showing ${filteredCount} of ${totalCount} clusters`;
}

function ClusterSettingsOverridesCard({
  entry,
  searchTerms,
}: {
  entry: ClusterSettingsOverridesEntry;
  searchTerms: string[];
}) {
  const { cluster, settingsOverrides, files, settingCount } = entry;

  return (
    <article className="overflow-hidden rounded-md border border-gray-200 bg-white">
      <Link
        to={`/clusters/${cluster.id}`}
        className="flex flex-col gap-1 border-b border-gray-200 bg-gray-50 px-3 py-2 transition-colors hover:bg-gray-100 sm:flex-row sm:items-center sm:justify-between"
      >
        <div className="min-w-0">
          <h4 className="truncate text-sm font-medium text-gray-900">
            <HighlightedText text={cluster.id} searchTerms={searchTerms} />
          </h4>
          <p className="text-xs text-gray-500">
            {formatCount(settingCount, "setting")} across{" "}
            {formatCount(files.length, "file")}
          </p>
        </div>
        <span className="text-xs font-medium text-blue-700">
          Inspect cluster
        </span>
      </Link>
      <div className="p-3">
        <SettingsOverridesOverview
          settingsOverrides={settingsOverrides}
          files={files}
          searchTerms={searchTerms}
        />
      </div>
    </article>
  );
}

function SettingsOverridesOverview({
  settingsOverrides,
  files,
  searchTerms,
}: {
  settingsOverrides: SettingsOverrides;
  files: ClusterSettingsOverridesFile[];
  searchTerms: string[];
}) {
  return (
    <div className="space-y-4">
      {files.map(({ fileName }) => (
        <section key={fileName} className="space-y-2">
          <h5 className="font-mono text-xs font-semibold text-gray-700">
            <HighlightedText text={fileName} searchTerms={searchTerms} />
          </h5>
          <YamlCodeBlock
            value={settingsOverrides[fileName] ?? ""}
            searchTerms={searchTerms}
          />
        </section>
      ))}
    </div>
  );
}

function buildSettingsOverridesEntry(
  cluster: Cluster,
): ClusterSettingsOverridesEntry[] {
  const settingsOverrides = cluster.settings_overrides;
  if (settingsOverrides == null) {
    return [];
  }

  const files = getSettingsOverrideFiles(settingsOverrides).map((fileName) =>
    buildSettingsOverridesFile(settingsOverrides, fileName),
  );
  const settingCount = sumFileSettings(files);
  if (settingCount === 0) {
    return [];
  }

  return [{ cluster, settingsOverrides, files, settingCount }];
}

function buildSettingsOverridesFile(
  settingsOverrides: SettingsOverrides,
  fileName: string,
): ClusterSettingsOverridesFile {
  const content = settingsOverrides[fileName] ?? "";
  return {
    fileName,
    searchText: `${fileName} ${content}`.toLowerCase(),
    settingCount: countSettingsOverrides({ [fileName]: content }),
  };
}

function sumFileSettings(files: ClusterSettingsOverridesFile[]) {
  return files.reduce((sum, file) => sum + file.settingCount, 0);
}

function HighlightedText({
  text,
  searchTerms,
}: {
  text: string;
  searchTerms: string[];
}) {
  return <>{highlightSearchText(text, searchTerms)}</>;
}

function formatCount(count: number, noun: string) {
  return `${count} ${noun}${count === 1 ? "" : "s"}`;
}
